// Shared navigation definitions; values and helpers are maintained here.



// --- Navigation destinations -------------------------------------------
// The feature registry lists backend CAPABILITIES (17+ of them: transports,
// ingest pipelines, observer adapters, auth methods...) — it is the source
// of truth for what's AVAILABLE, never a 1:1 list of nav items. Rendering
// every enabled feature id as its own nav
// button produced a two-row wall of internal capability names ("Config
// API", "Retention & Rollup Jobs", "CoreScope Observer"...) that isn't
// navigation at all. This is the curated, small set of real destinations a
// person actually goes to; each is gated on whether the feature(s) backing
// it are enabled (and, where noted, on role), so a destination with nothing
// behind it is simply absent — same "never offer what isn't there" rule,
// applied one level up from individual features.
export interface NavDestination {
  id: string;
  label: string;
  // Enabled if ANY of these feature ids is enabled (most destinations are
  // backed by exactly one; a couple reasonably read multiple).
  requiresAnyOf: string[];
  route: "map" | "feed" | "status" | "nodes" | "mqtt" | "settings" | "audit" | "messages" | "hazards";
  adminOnly?: boolean;
}


export const NAV_DESTINATIONS: NavDestination[] = [
  { id: "live_map", label: "Live Map", requiresAnyOf: ["core_ingest", "live_stream"], route: "map" },
  { id: "nodes", label: "Nodes", requiresAnyOf: ["node_list"], route: "nodes" },
  { id: "packets", label: "Packets", requiresAnyOf: ["packet_view"], route: "feed" },
  { id: "mqtt", label: "MQTT", requiresAnyOf: ["mqtt_ingest"], route: "mqtt" },
  // Coverage retired as its own destination and folded into reachability
  // under audit so it makes more sense structurally — its content now lives under Audit >
  // Reachability (AuditPage.tsx). #/coverage redirects there (router.ts)
  // rather than 404ing an old bookmark.
  { id: "audit", label: "Analysis", requiresAnyOf: ["audit_detectors"], route: "audit" },
  { id: "messages", label: "Messages", requiresAnyOf: ["simulated_nodes", "message_send"], route: "messages" },
  { id: "health", label: "Health", requiresAnyOf: ["health_monitoring"], route: "status" },
  // Renamed to "Alerts" per the IA spec: hazards should be alerts and
  // contain tabs for broadcast, regions, and sources (see
  // HazardsPage.tsx). Display-only: the route id, backend feature id
  // (hazard_adapters), and every API path stay "hazards" — that's the
  // real, permanent backend contract, not something this rename touches.
  { id: "hazards", label: "Alerts", requiresAnyOf: ["hazard_adapters"], route: "hazards" },
  { id: "settings", label: "Settings", requiresAnyOf: ["config_api"], route: "settings", adminOnly: true },
];


// The fragmentation preview used to be a client-side estimate (200
// bytes/fragment, protocol-agnostic) because the contract had no dry-run
// endpoint. That gap was closed 2026-09-20: POST /api/messages/preview
// now returns the backend's own real fragment split — same shape as
// /api/messages/send's response, computed against the actual per-protocol
// byte budget (Meshtastic and MeshCore differ) rather than guessed here.
// See pages/MessagesPage.tsx for the call; nothing in this file estimates
// fragmentation anymore.

// --- Paging (phase 4 amendment) --------------------------------------------
// Measured directly against real data, 2026-09-20: Audit rendered
// 1,840 findings as one 192,000px-tall page, Coverage 1,405 rows at
// 46,627px — unusable. The audit/coverage list endpoints now accept
// limit/cursor and return total/next_cursor; PAGE_SIZE is the limit this
// frontend always requests (the contract's stated default, and comfortably
// under its max of 500). See api/usePagedFetch.ts for the cursor-stack
// Prev/Next mechanics built against this.
export const PAGE_SIZE = 50;


// --- No-connections-configured empty state (fresh-install sweep) ----------
// Shared copy for any page (Live Map, Nodes, Packets, Node Configuration)
// that has nothing to show because no radio connection has been added yet
// — as opposed to "nothing matches the current filters." One line, defined
// once, so every page points the operator to the same place.
export const NO_CONNECTIONS_EMPTY_STATE = "Add a Meshtastic or MeshCore connection in Settings → Connections to get started.";
